import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Admin.css';

function AdminDashboard() {
  const [requests, setRequests] = useState([]);
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [newAdminUsername, setNewAdminUsername] = useState('');
  const [createMessage, setCreateMessage] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordMessage, setPasswordMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = localStorage.getItem('adminSessionId');
    const storedUsername = localStorage.getItem('adminUsername');

    if (!sessionId || !storedUsername) {
      navigate('/admin/login');
      return;
    }

    setUsername(storedUsername);
    fetchDemoRequests(sessionId);
  }, [navigate]);

  const fetchDemoRequests = async (sessionId) => {
    try {
      const response = await fetch(`http://localhost:5000/api/admin/demo-requests?sessionId=${sessionId}`);
      const data = await response.json();

      if (data.success) {
        setRequests(data.requests);
      } else {
        setError('Failed to fetch requests');
        if (response.status === 401) {
          navigate('/admin/login');
        }
      }
    } catch (err) {
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminSessionId');
    localStorage.removeItem('adminUsername');
    navigate('/admin/login');
  };

  const handleOpenWhatsApp = (phone, name, company) => {
    const message = `Hi ${name} from ${company}, I'm following up on your demo request for Systems Installation. When would be a good time to discuss this?`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phone.replace(/\D/g, '')}?text=${encodedMessage}`, '_blank');
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    const sessionId = localStorage.getItem('adminSessionId');

    try {
      const response = await fetch('http://localhost:5000/api/admin/create-admin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, newUsername: newAdminUsername })
      });

      const data = await response.json();

      if (data.success) {
        setCreateMessage(`✓ Admin '${newAdminUsername}' created successfully!`);
        setNewAdminUsername('');
        setTimeout(() => {
          setShowCreateAdmin(false);
          setCreateMessage('');
        }, 2000);
      } else {
        setCreateMessage(`✗ ${data.error || 'Error creating admin'}`);
      }
    } catch (err) {
      setCreateMessage('Error connecting to server');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    const sessionId = localStorage.getItem('adminSessionId');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage('✗ New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordMessage('✗ Password must be at least 6 characters');
      return;
    }

    try {
      const response = await fetch('http://localhost:5000/api/admin/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          oldPassword: passwordForm.oldPassword,
          newPassword: passwordForm.newPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        setPasswordMessage('✓ Password changed successfully!');
        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => {
          setShowChangePassword(false);
          setPasswordMessage('');
        }, 2000);
      } else {
        setPasswordMessage(`✗ ${data.error || 'Error changing password'}`);
      }
    } catch (err) {
      setPasswordMessage('Error connecting to server');
    }
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return <div className="admin-container"><p>Loading...</p></div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div className="header-left">
          <h1>Admin Dashboard</h1>
          <p>Welcome, <strong>{username}</strong></p>
        </div>
        <div className="header-right">
          <button className="create-admin-btn" onClick={() => setShowCreateAdmin(!showCreateAdmin)}>
            + Create Admin
          </button>
          <button className="change-password-btn" onClick={() => setShowChangePassword(!showChangePassword)}>
            🔐 Change Password
          </button>
          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      {showCreateAdmin && (
        <div className="create-admin-box">
          <h3>Create New Admin</h3>
          {createMessage && <p className={createMessage.includes('✓') ? 'success' : 'error'}>{createMessage}</p>}
          <form onSubmit={handleCreateAdmin}>
            <input
              type="text"
              placeholder="New Admin Username"
              value={newAdminUsername}
              onChange={(e) => setNewAdminUsername(e.target.value)}
              required
            />
            <button type="submit">Create Admin</button>
          </form>
        </div>
      )}

      {showChangePassword && (
        <div className="change-password-box">
          <h3>Change Password</h3>
          {passwordMessage && <p className={passwordMessage.includes('✓') ? 'success' : 'error'}>{passwordMessage}</p>}
          <form onSubmit={handlePasswordChange}>
            <input
              type="password"
              name="oldPassword"
              placeholder="Current Password"
              value={passwordForm.oldPassword}
              onChange={handlePasswordInputChange}
              required
            />
            <input
              type="password"
              name="newPassword"
              placeholder="New Password"
              value={passwordForm.newPassword}
              onChange={handlePasswordInputChange}
              required
            />
            <input
              type="password"
              name="confirmPassword"
              placeholder="Confirm New Password"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordInputChange}
              required
            />
            <button type="submit">Change Password</button>
          </form>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <div className="requests-section">
        <h2>Demo Requests ({requests.length})</h2>

        {requests.length === 0 ? (
          <p className="no-requests">No demo requests yet</p>
        ) : (
          <div className="requests-table">
            {requests.map((request) => (
              <div key={request.id} className="request-card">
                <div className="request-header">
                  <h3>{request.name}</h3>
                  <span className={`status ${request.status}`}>{request.status}</span>
                </div>
                <div className="request-details">
                  <p><strong>Service:</strong> {request.service}</p>
                  <p><strong>Company:</strong> {request.company}</p>
                  <p><strong>Email:</strong> <a href={`mailto:${request.email}`}>{request.email}</a></p>
                  <p><strong>Phone:</strong> {request.phone}</p>
                  <p><strong>Date:</strong> {new Date(request.createdAt).toLocaleString()}</p>
                </div>
                <div className="request-actions">
                  <button
                    className="whatsapp-btn"
                    onClick={() => handleOpenWhatsApp(request.phone, request.name, request.company)}
                  >
                    💬 Message on WhatsApp
                  </button>
                  <a href={`mailto:${request.email}`} className="email-btn">
                    ✉️ Send Email
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
